import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Route } from 'react-router-dom';
import { isNilOrEmpty } from '../util/redux';
import LogRocket from 'logrocket';

import Authed from './Authed';
import Guest from './Guest';

const mapStore = (store) => ({
  user: store.user,
});

const propTypes = {
  user: PropTypes.object,
};

const Routes = ({ user }) => {
  if (isNilOrEmpty(user)) return <Route path="/" component={Guest} />;

  if (window.env.REACT_APP_ENV == 'production') {
    LogRocket.identify(user.id, {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
    });
  }

  try {
    localStorage.removeItem('referrer');
  } catch (err) {
    console.log(err);
  }
  return <Route path="/" render={() => <Authed user={user} />} />;
};

export const routes = {
  // Global routes
  findProfessional: '/find-a-professional',

  // Guest routes
  login: '/login',
  register: '/register',
  forgotPassword: '/password/reset',
  resetPassword: '/password/reset/:token',

  // OAuth routes
  oauthCanvas: '/oauth/canvas',
  oauthCanvasLogout: '/oauth/canvas/logout',

  // WP routes
  wordPress: {
    afcCompetencies: `${window.env.REACT_WP_DOMAIN}/certification-and-training/accredited-financial-counselor/afc-core-competencies/`,
    ampAccommodations: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2018/11/ADA-Forms-888-2015.pdf`,
    afcHandbook: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2019/03/AFCPE-Candidate-Handbook-2019.pdf`,
    // TODO: Add in correct link
    certAgreement: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2019/03/AFCPE-Candidate-Handbook-2019.pdf`,
    examPledge: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2020/08/AFCPE-Certification-Exam-Code-of-Conduct.pdf`,
    employmentVerification: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2020/07/AFCPE-Employment-Verification-Form-fill-1.pdf`,
    findAProfessional: `${window.env.REACT_WP_DOMAIN}/find-an-afcpe-certified-professional/`,
    membership: `${window.env.REACT_WP_DOMAIN}/membership/`,
    membershipBenefits: `${window.env.REACT_WP_DOMAIN}/membership/#benefits`,
    mentorConnect: `${window.env.REACT_WP_DOMAIN}/membership/mentorship/`,
    newsletters: `${window.env.REACT_WP_DOMAIN}/news-and-publications/the-standard/`,
    mme: `${window.env.REACT_WP_DOMAIN}/certification-and-training/money-management-essentials/`,
    mmeForm: 'https://afcpe-rcfrk.formstack.com/forms/money_management_essentials_enrollment',
    mentorConnectForm: `${window.env.REACT_WP_DOMAIN}/membership/mentorship/signup/`,
    mentorshipForm: 'https://www.surveymonkey.com/r/JVVL2ZD',
    termsOfUse: `${window.env.REACT_WP_DOMAIN}/about/terms-of-use/`,
    privacyPolicy: `${window.env.REACT_WP_DOMAIN}/about/privacy-policy/`,
    bylaws: `${window.env.REACT_WP_DOMAIN}/about/bylaws/`,
    refundPolicy: `${window.env.REACT_WP_DOMAIN}/wp-content/uploads/2020/05/AFCPE-Refund-and-Extension-Policy-5-2020.pdf`,
  },

  // Authed routes
  dashboard: '/',
  dashboardPaid: '/?paid=true',
  donate: '/donate',
  payments: {
    root: '/payments',
    receipt: '/payments/:id',
    receiptBuilder: (id) => `/payments/${id}`,
  },
  editProfile: {
    root: '/edit-profile',
    accountSettings: '/edit-profile/account',
    professionalInfo: '/edit-profile/professional',
    afcpeSettings: '/edit-profile/afcpe',
    canvasSettings: '/edit-profile/canvas',
    socialDetails: '/edit-profile/social',
  },
  certificationsAndTraining: {
    root: '/certifications-and-training',
    register: {
      root: '/certifications-and-training/register',
      training: '/certifications-and-training/register/training/:type',
      trainingBuilder: (type) => `/certifications-and-training/register/training/${type}`,
      trainingPaidBuilder: (type) => `/certifications-and-training/register/training/${type}?paid=true`,
      type: '/certifications-and-training/register/:type',
      typeBuilder: (type) => `/certifications-and-training/register/${type}`,
      typePaidBuilder: (type) => `/certifications-and-training/register/${type}?paid=true`,
    },
    certifications: {
      root: '/certifications-and-training/certifications',
      view: '/certifications-and-training/certifications/:id',
      pay: '/certifications-and-training/certifications/pay',
      viewBuilder: (id) => `/certifications-and-training/certifications/${id}`,
    },
    trainings: {
      root: '/certifications-and-training/trainings',
      view: '/certifications-and-training/trainings/:id',
      viewBuilder: (id) => `/certifications-and-training/trainings/${id}`,
    },
    extension: {
      root: '/certifications-and-training/extensions',
      paid: '/certifications-and-training/extensions?paid=true',
      request: '/certifications-and-training/extensions/request',
    },
    experience: {
      root: '/certifications-and-training/experience',
      add: '/certifications-and-training/experience/new',
      edit: '/certifications-and-training/experience/:id',
      editBuilder: (id) => `/certifications-and-training/experience/${id}`,
    },
  },
  membership: {
    root: '/membership',
    paid: '/membership?paid=true',
    resources: '/membership#membership-resources',
    renew: '/membership/get',
    search: '/membership/search',
    forum: '/forums',
  },
  ceus: {
    root: '/ceus',
    individual: {
      root: '/ceus/individual',
      add: '/ceus/individual/new',
      edit: '/ceus/individual/:id',
      editBuilder: (id) => `/ceus/individual/${id}`,
    },
    group: {
      root: '/ceus/group',
      add: '/ceus/group/new',
      edit: '/ceus/group/:id',
      editBuilder: (id) => `/ceus/group/${id}`,
    },
  },
  events: {
    root: '/events',
    upcoming: '/events/upcoming',
    view: '/events/:id',
    viewBuilder: (id) => `/events/${id}`,
    paidBuilder: (id) => `/events/${id}?paid=true`,
  },

  // Admin routes
  admin: {
    dashboard: '/admin',
    users: {
      root: '/admin/users',
      profile: {
        root: '/admin/users/:id',
        registrations: '/admin/users/:id/registrations',
        newRegistration: '/admin/users/:id/registrations/new',
        certifications: '/admin/users/:id/certifications',
        certification: '/admin/users/:userId/certifications/:id',
        memberships: '/admin/users/:id/memberships',
        notes: '/admin/users/:id/notes',
        flags: '/admin/users/:id/flags',
        payments: '/admin/users/:id/payments',
        groups: '/admin/users/:id/groups',
        rootBuilder: (id) => `/admin/users/${id}`,
        registrationsBuilder: (id) => `/admin/users/${id}/registrations`,
        newRegistrationsBuilder: (id) => `/admin/users/${id}/registrations/new`,
        certificationsBuilder: (id) => `/admin/users/${id}/certifications`,
        certificationBuilder: (userId, certificationId) => `/admin/users/${userId}/certifications/${certificationId}`,
        membershipsBuilder: (id) => `/admin/users/${id}/memberships`,
        notesBuilder: (id) => `/admin/users/${id}/notes`,
        flagsBuilder: (id) => `/admin/users/${id}/flags`,
        paymentsBuilder: (id) => `/admin/users/${id}/payments`,
        groupsBuilder: (id) => `/admin/users/${id}/groups`,
      },
      profileBuilder: (id) => `/admin/users/${id}`,
    },
    courseRegistrations: {
      root: '/admin/course-registrations',
      review: '/admin/course-registrations/:id',
      reviewBuilder: (id) => `/admin/course-registrations/${id}`,
    },
    individualCeus: {
      root: '/admin/ceus/individual',
      review: '/admin/ceus/individual/:id',
      reviewBuilder: (id) => `/admin/ceus/individual/${id}`,
    },
    groupCeus: {
      root: '/admin/ceus/group',
      review: '/admin/ceus/group/:id',
      reviewBuilder: (id) => `/admin/ceus/group/${id}`,
    },
    experiences: {
      root: '/admin/experiences',
      review: '/admin/experiences/:id',
      reviewBuilder: (id) => `/admin/experiences/${id}`,
    },
    extensions: {
      root: '/admin/extensions',
      review: '/admin/extensions/:id',
      reviewBuilder: (id) => `/admin/extensions/${id}`,
    },
    memberships: {
      root: '/admin/memberships',
      review: '/admin/memberships/:id',
      reviewBuilder: (id) => `/admin/memberships/${id}`,
    },
    backpack: {
      root: '/admin/extended',
      announcements: '/admin/extended/announcement',
      certifications: '/admin/extended/certification',
      certificationRequirements: '/admin/extended/certification-requirement',
      certificationResources: '/admin/extended/certification-resource',
      courseMaterials: '/admin/extended/course-material',
      creditTypes: '/admin/extended/credit-type',
      pathways: '/admin/extended/pathway',
      emailDropdown: '/admin/extended/email-dropdown',
      events: '/admin/extended/event',
      exams: '/admin/extended/exam',
      forum: '/admin/extended/forum-category',
      discounts: '/admin/extended/discount',
      groups: '/admin/extended/group',
      memberships: '/admin/extended/membership',
      membershipResources: '/admin/extended/membership-resource',
      militaryInstallations: '/admin/extended/military-installation',
      quickbooksProducts: '/admin/extended/quickbooks-product',
      registrationFees: '/admin/extended/registration-fee',
      reportingPeriods: '/admin/extended/reporting-period',
      serviceTypes: '/admin/extended/service-type',
      trainings: '/admin/extended/training',
      universities: '/admin/extended/university',
      workAreas: '/admin/extended/work-area',
      workAreaCategories: '/admin/extended/work-area-category',
    },
  },
};

Routes.propTypes = propTypes;

export default withRouter(connect(mapStore)(Routes));
